// LocationView — everything you can DO where you stand: the extraction
// strip (gather/mine/chop/fish in DEDICATED fixed slots — absent kinds leave
// quiet placeholders so kinds never move between locations) plus Hunt under
// its own "starts a fight" heading, plus the tile's NPC card. While a fight
// is up the fight panel REPLACES the strip (no combat tab).

import { clear, el } from '../kit';
import * as icons from '../icons';
import * as ui from '../ui';
import * as vsj from '../vsj';
import { activityCard, CardHandle } from '../components/activity_card';
import { fightPanel } from '../components/fight_panel';
import { kbtn } from '../kit';

// The strip layout is SERVED (vsj.location_strip.slots): the driver owns
// the work-kind roster, slot order and placeholder copy — this view renders
// the served slot list verbatim (hunt splits into its own row: layout only).

export function locationView(): {
  root: HTMLElement;
  refresh(changed: Set<string>): void;
  onShown(): void;
} {
  const heading = el('div', { class: 'k-small k-faint', text: 'Around here' });
  const workFlow = el('div', { class: 'strip-row' });
  const huntHeading = el('div', { class: 'k-small k-faint', text: 'Hunting — starts a fight' });
  const huntFlow = el('div', { class: 'strip-row' });
  const npcHeading = el('div', { class: 'k-small k-faint', text: 'Someone here' });
  const npcHolder = el('div');

  // persistent per-kind holders, created lazily in SERVED order
  const slots: Record<string, HTMLElement> = {};
  const holderFor = (kind: string): HTMLElement => {
    if (!slots[kind]) {
      const holder = el('div');
      (kind === 'hunt' ? huntFlow : workFlow).append(holder);
      slots[kind] = holder;
    }
    return slots[kind];
  };

  const cards: Record<string, CardHandle> = {};
  const placeholders: Record<string, HTMLElement> = {};

  const strip = el(
    'div',
    { class: 'extraction-strip' },
    workFlow,
    huntHeading,
    huntFlow,
    npcHeading,
    npcHolder,
  );
  const fight = fightPanel();
  fight.root.style.display = 'none';
  const root = el(
    'div',
    { class: 'k-panel view-body', style: 'padding:8px;display:flex;flex-direction:column;gap:6px' },
    heading,
    strip,
    fight.root,
  );

  const makePlaceholder = (name: string, text: string): HTMLElement =>
    el(
      'div',
      { class: 'strip-placeholder', style: 'padding:8px' },
      el('div', { class: 'k-faint', text: name }),
      el('div', { class: 'k-small k-faint', text }),
    );

  const refreshCards = () => {
    const acts = vsj.at('activities') ?? {};
    const glow: string[] = vsj.at('guidance.glow') ?? [];
    const served: any[] = vsj.at('location_strip.slots') ?? [];
    let any = false;
    const seen = new Set<string>();
    for (const entry of served) {
      if (!entry || typeof entry !== 'object') continue;
      const kind = String(entry.kind ?? '');
      seen.add(kind);
      const holder = holderFor(kind);
      const a = acts[kind];
      if (a && typeof a === 'object') {
        any = true;
        if (placeholders[kind]) {
          placeholders[kind].remove();
          delete placeholders[kind];
        }
        if (!cards[kind]) {
          const card = activityCard(true, kind === 'hunt' ? 'var(--c-creature)' : '');
          holder.append(card.root);
          cards[kind] = card;
        }
        cards[kind].update(kind, a, glow);
      } else {
        if (cards[kind]) {
          cards[kind].root.remove();
          delete cards[kind];
        }
        if (entry.placeholder != null && !placeholders[kind]) {
          const ph = makePlaceholder(String(entry.name ?? kind), String(entry.placeholder));
          holder.append(ph);
          placeholders[kind] = ph;
        }
      }
    }
    // kinds the server no longer lists drop out entirely
    for (const kind of Object.keys(slots)) {
      if (seen.has(kind)) continue;
      cards[kind]?.root.remove();
      delete cards[kind];
      placeholders[kind]?.remove();
      delete placeholders[kind];
    }
    strip.style.visibility = any ? '' : 'hidden';
  };

  const refreshNpc = () => {
    clear(npcHolder);
    const npc = vsj.at('npc');
    const has = npc && typeof npc === 'object';
    npcHeading.style.display = has ? '' : 'none';
    if (!has) return;
    const nameLbl = el('span', { text: String(npc.name ?? '?') });
    if (npc.info) {
      nameLbl.style.cursor = 'pointer';
      nameLbl.addEventListener('click', () => ui.select(String(npc.info)));
    }
    const card = el(
      'div',
      {
        class: 'k-panel',
        style: 'width:var(--card-width-location);padding:8px;display:flex;flex-direction:column;gap:4px',
      },
      el('div', { style: 'display:flex;gap:6px;align-items:center' }, icons.plated(String(npc.icon ?? 'npc'), 40, 4), nameLbl),
      el('div', { class: 'k-small k-dim', text: `“${npc.text ?? ''}”` }),
    );
    const quest = npc.quest;
    if (quest && typeof quest === 'object') {
      card.append(el('div', { class: 'c-quest', text: String(quest.name ?? '') }));
      if (quest.goal != null) card.append(el('div', { class: 'k-small k-dim', text: String(quest.goal) }));
      if (quest.reward_text != null)
        card.append(el('div', { class: 'k-small k-dim', text: 'Reward: ' + quest.reward_text }));
    }
    const status = String(npc.status ?? 'open');
    if (status === 'open')
      card.append(
        el('div', { style: 'display:flex;gap:6px' },
          kbtn(npc.accept, { label: 'Accept' }),
          kbtn(npc.deny, { label: 'No thanks' })),
      );
    else
      card.append(
        el('div', { class: 'k-small k-faint', text: status === 'accepted' ? "You're on it." : 'You said no.' }),
      );
    npcHolder.append(card);
  };

  const refreshFightSwap = () => {
    const fighting = !!vsj.at('fight');
    fight.root.style.display = fighting ? '' : 'none';
    strip.style.display = fighting ? 'none' : '';
    heading.style.display = fighting ? 'none' : '';
  };

  refreshCards();
  refreshNpc();
  refreshFightSwap();

  return {
    root,
    refresh(changed) {
      const all = changed.has('*');
      if (all || changed.has('activities') || changed.has('location_strip') || changed.has('guidance') || changed.has('infos')) refreshCards();
      if (all || changed.has('npc')) refreshNpc();
      if (all || changed.has('fight')) refreshFightSwap();
      fight.refresh(changed);
    },
    // "take me there": a jump landed here pointing at one card — flash it
    onShown() {
      const key = ui.consumeFlash('location');
      if (key && cards[key]) ui.flashTarget(cards[key].root);
    },
  };
}
